import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Invoice } from 'src/app/core/interfaces/invoice.interface';
import { LocalizationService } from '../../services/localization.service';
import { Subject, takeUntil } from 'rxjs';
import { PaymentVoucherModalComponent } from 'src/app/modules/invoices/create-invoice/payment-voucher-modal/payment-voucher-modal.component';
@Component({
  selector: 'app-payment-history-modal',
  templateUrl: './payment-history-modal.component.html',
  styleUrls: ['./payment-history-modal.component.scss'],
})
export class PaymentHistoryModalComponent implements OnInit {
  @Input() invoice: Invoice | null = null;
  @Input() paymentHistory: any[] = [];
  @Input() isReceiptVoucher: boolean = true;
  destroy$ = new Subject<void>();
  displayedPayments: any[] = [];
  totalPaid: number = 0;
  totalRemaining: number = 0;

  /** True while the invoice @Input has not yet been set by the parent */
  get isInvoiceLoading(): boolean {
    return this.invoice == null;
  }

  constructor(
    public activeModal: NgbActiveModal,
    private localaztionService: LocalizationService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.processPaymentHistory();
  }

  private processPaymentHistory(): void {
    if (!this.paymentHistory || this.paymentHistory.length === 0) {
      this.displayedPayments = [];
      this.totalPaid = this.getTotalInvoicePaidAmount();
      this.totalRemaining = this.gettotalRemainingAmount();
      return;
    }

    this.displayedPayments = this.paymentHistory.map((payment, index) => ({
      ...payment,
      sequence: index + 1,
    }));

    this.totalPaid = this.getTotalInvoicePaidAmount();
    this.totalRemaining = this.gettotalRemainingAmount();
  }

  private openReceiptVoucherModal(invoice: Invoice): void {
    const modalRef = this.modalService.open(PaymentVoucherModalComponent, {
      size: 'lg',
      backdrop: 'static',
      centered: true,
      windowClass: 'payment-voucher-modal-window',
    });

    modalRef.componentInstance.invoiceId = invoice.id;
    modalRef.componentInstance.invoiceSerialNumber = invoice.serialNumber;
    modalRef.componentInstance.invoiceContactId = invoice.contactId ?? null;
    modalRef.componentInstance.invoiceContactName = invoice.contactName || '';
    modalRef.componentInstance.invoiceTotalAmount =
      invoice.totalAmount ?? invoice.TotalAmount ?? 0;
    modalRef.componentInstance.invoicePushedAmount =
      invoice.pushedAmount ?? (invoice as any).PushedAmount ?? 0;
    modalRef.componentInstance.reminderAmount = this.getInvoiceRemaining(invoice);
    modalRef.componentInstance.invoiceRemaining = this.getInvoiceRemaining(invoice);

    modalRef.componentInstance.invoiceCurrencyId = invoice.currencyId;
    modalRef.componentInstance.invoiceDate = this.formatInvoiceDate(
      invoice.documentDate
    );
    modalRef.componentInstance.invoiceNote = invoice.note || '';
    modalRef.componentInstance.isReceiptVoucher = this.isReceiptVoucher;
    modalRef.componentInstance.invoiceCurrencySymbol =
      invoice.currency?.symbol || '';
    modalRef.closed.pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      if (result?.created) {
        this.activeModal.close({ created: true });
      }
    });
  }

  private getTotalAmount(): number {
    if (!this.invoice) return 0;
    const net = this.invoice.net ?? this.invoice.totalAmount ?? 0;
    return Number(net);
  }
   private gettotalRemainingAmount(): number {
     return this.getInvoiceRemaining(this.invoice);
  }
   getTotalInvoicePaidAmount(): number {
    if (!this.invoice) return 0;

    const net = this.invoice.pushedAmount;
    return Number(net);
  }
  getInvoicePaidAmount(): number {
    if (!this.invoice) return 0;
    const net = this.invoice.pushedAmount ?? this.invoice.pushedAmount ?? 0;
    return Number(net);
  }
  private formatInvoiceDate(date: Date | string): string {
    if (!date) {
      return new Date().toISOString().split('T')[0];
    }
    const parsed = typeof date === 'string' ? new Date(date) : date;
    return isNaN(parsed.getTime())
      ? new Date().toISOString().split('T')[0]
      : parsed.toISOString().split('T')[0];
  }
  getInvoiceRemaining(invoice: Invoice | null | undefined): number {
    if (!invoice) {
      return 0;
    }

    if (invoice.remainderAmount !== 0 && invoice.remainderAmount !== undefined) {
      return Number(invoice.remainderAmount);
    }

    if ((invoice.remainderAmount === 0 || invoice.remainderAmount === undefined) && invoice.pushedAmount === invoice.totalAmount) {
      return 0;
    }

    if (invoice.pushedAmount !== invoice.totalAmount) {
      const remaining = this.getTotalAmountPublic() - Number(invoice.pushedAmount || 0);
      return typeof remaining === 'number' && !isNaN(remaining) && remaining >= 0
        ? remaining
        : 0;
    }

    return 0;
  }
  getTotalAmountPublic(): number {
    return this.getTotalAmount();
  }

  getPaymentMethodIcon(method: string): string {
    if (!method) return 'fa-coins';
    const lowerMethod = method.toLowerCase();
    if (lowerMethod.includes('cash') || lowerMethod.includes('نقدي'))
      return 'fa-money-bill-wave';
    if (lowerMethod.includes('card') || lowerMethod.includes('بطاقة'))
      return 'fa-credit-card';
    if (lowerMethod.includes('bank') || lowerMethod.includes('تحويل'))
      return 'fa-university';
    if (lowerMethod.includes('cheque') || lowerMethod.includes('شيك'))
      return 'fa-money-check';
    return 'fa-coins';
  }

  getPaymentStatusClass(payment: any): string {
    const remaining = Number(payment.remainingAmount) || 0;

    if (remaining === 0) return 'status-paid';
    if (remaining > 0) return 'status-partial';
    return 'status-unpaid';
  }

  getStatusBadgeText(payment: any): string {
    const remaining = Number(payment.remainingAmount) || 0;
    if (remaining === 0) return 'مدفوعة بالكامل';
    if (remaining > 0) return 'مدفوعة جزئياً';
    return 'لم تدفع';
  }

  formatDate(dateStr: string | Date | undefined): string {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return String(dateStr);
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  getCurrencySymbol(): string {
    if (!this.invoice) return 'ر.س';
    const currency = this.invoice.currency?.symbol || '';
    if (currency.includes('USD') || currency.includes('DOLLAR')) return '$';
    if (currency.includes('EUR')) return '€';
    return 'ر.س';
  }
}
